import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Copy, Check as CheckIcon, Share2, Download, Star, ArrowLeft, ExternalLink, Lock,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const buildCaption = (recap) => {
  const num = recap.case_number || "001";
  const title = (recap.case_title || "A murder mystery").toUpperCase();
  const victim = recap.victim_name || "?";
  const ref = recap.referral_code ? ` Use my code ${recap.referral_code} for $5 off → wasntme.party` : " → wasntme.party";
  return `We solved CASE #${num} — ${title}. The victim: ${victim}. 🕯️🔪\n\nPersonalized murder mystery kits at wasntme.party — a @nightbloodchronicles production.${ref}\n\n#WasntMe.Party #murdermystery #dinnerparty`;
};

const RATINGS = [
  { key: "unforgettable", label: "Unforgettable", emoji: "🖤" },
  { key: "great", label: "Great", emoji: "✨" },
  { key: "okay", label: "Okay", emoji: "👍" },
  { key: "not_for_us", label: "Not for us", emoji: "😐" },
];

const RecapPage = () => {
  const { hostToken } = useParams();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [survey, setSurvey] = useState({ rating: "", biggest_liar: "", feature_permission: false, comment: "", first_names_only: false });
  const [surveyDone, setSurveyDone] = useState(false);
  const [showShares, setShowShares] = useState(false);
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/host/${hostToken}/recap`).then((r) => {
      if (!alive) return;
      setRecap(r.data);
      if (r.data.survey) {
        setSurvey({
          rating: r.data.survey.rating || "",
          biggest_liar: r.data.survey.biggest_liar || "",
          feature_permission: !!r.data.survey.feature_permission,
          comment: r.data.survey.comment || "",
          first_names_only: !!r.data.first_names_only,
        });
        setSurveyDone(true);
        setShowShares(["great", "unforgettable"].includes(r.data.survey.rating));
      } else {
        setSurvey((s) => ({ ...s, first_names_only: !!r.data.first_names_only }));
      }
    }).catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    api.post("/events", { event: "recap_opened", host_token: hostToken }).catch(() => {});
    return () => { alive = false; };
  }, [hostToken]);

  const submitSurvey = async () => {
    if (!survey.rating) {
      toast.error("Pick a rating first.");
      return;
    }
    setSavingSurvey(true);
    try {
      const r = await api.post(`/host/${hostToken}/survey`, survey);
      setSurveyDone(true);
      setShowShares(!!r.data.should_show_shares);
      toast.success("Thanks for the verdict.");
      // Refresh so the Biggest Liar flows into the Verdict Lawyer slot
      api.get(`/host/${hostToken}/recap`).then((rr) => setRecap(rr.data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setSavingSurvey(false);
    }
  };

  const copyCaption = async () => {
    if (!recap) return;
    try {
      await navigator.clipboard.writeText(buildCaption(recap));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Caption copied.");
    } catch {
      toast.error("Couldn't copy. Long-press the caption to copy manually.");
    }
    api.post("/events", { event: "card_saved", host_token: hostToken, props: { action: "copy_caption" } }).catch(() => {});
  };

  const nativeShare = async () => {
    if (!navigator.share || !recap) {
      copyCaption();
      return;
    }
    try {
      await navigator.share({
        title: `Case #${recap.case_number} · solved`,
        text: buildCaption(recap),
        url: recap.referral_url || "https://wasntme.party",
      });
      api.post("/events", { event: "share_clicked", host_token: hostToken, props: { method: "native" } }).catch(() => {});
    } catch { /* user cancelled */ }
  };

  const downloadImage = async (aspect) => {
    try {
      const res = await fetch(`${API_BASE}/api/host/${hostToken}/kit/certificate?aspect=${aspect}`);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `case-closed-${aspect}.png`;
      a.click();
      URL.revokeObjectURL(url);
      api.post("/events", { event: "card_saved", host_token: hostToken, props: { aspect } }).catch(() => {});
    } catch {
      toast.error("Download failed.");
    }
  };

  const copyRefLink = () => {
    if (!recap?.referral_url) return;
    navigator.clipboard.writeText(recap.referral_url);
    toast.success("Referral link copied.");
    api.post("/events", { event: "share_clicked", host_token: hostToken, props: { method: "ref_link" } }).catch(() => {});
  };

  if (loading) {
    return <div className="grain min-h-screen flex items-center justify-center"><Loader2 size={28} className="animate-spin text-[#d4af37]" /></div>;
  }
  if (notFound) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6" data-testid="recap-not-found">
        <div className="card-noir p-8 max-w-md w-full text-center">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-2">lost page</div>
          <h1 className="font-display text-3xl mb-3">Recap not found</h1>
          <p className="text-[#a39797] mb-6">This recap link may have expired. Ask your host for a fresh one.</p>
          <Link to="/" className="btn-primary"><ArrowLeft size={14} /> wasntme.party</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="recap-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">case closed</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">
          Nice work, {recap.host_name || "detective"}.
        </h1>
        <p className="text-[#a39797] mb-10 text-base">
          Everyone's still talking about {recap.case_title || "last night"}. Here's your recap — and your Verdict Cards.
        </p>

        {/* 3-question survey */}
        {!surveyDone && (
          <div className="card-noir p-6 mb-10" data-testid="survey">
            <div className="text-xs tracking-widest text-[#d4af37] mb-4">30-SECOND VERDICT</div>
            <div className="mb-5">
              <div className="font-display text-xl mb-3">How was it?</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {RATINGS.map((r) => (
                  <button key={r.key}
                    onClick={() => setSurvey((s) => ({ ...s, rating: r.key }))}
                    className={`py-3 px-2 rounded-md border text-sm transition-colors ${survey.rating === r.key ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-[#2d2424] text-[#a39797] hover:text-[#f4f0ec]"}`}
                    data-testid={`survey-rating-${r.key}`}>
                    <div className="text-xl mb-1">{r.emoji}</div>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {survey.rating && (
              <>
                <div className="mb-5">
                  <div className="font-display text-xl mb-3">Who was the biggest liar at your table?</div>
                  <select
                    value={survey.biggest_liar}
                    onChange={(e) => setSurvey((s) => ({ ...s, biggest_liar: e.target.value }))}
                    className="input-noir text-sm"
                    data-testid="survey-biggest-liar">
                    <option value="">— pick a name —</option>
                    {(recap.players || []).map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                </div>
                <label className="flex items-start gap-3 cursor-pointer mb-4">
                  <input type="checkbox"
                    checked={survey.feature_permission}
                    onChange={(e) => setSurvey((s) => ({ ...s, feature_permission: e.target.checked }))}
                    className="mt-1 accent-[#8b0000]"
                    data-testid="survey-feature-permission" />
                  <span className="text-sm text-[#a39797]">Can we feature your group? We'd love to repost your cards.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer mb-5">
                  <input type="checkbox"
                    checked={survey.first_names_only}
                    onChange={(e) => setSurvey((s) => ({ ...s, first_names_only: e.target.checked }))}
                    className="mt-1 accent-[#8b0000]"
                    data-testid="survey-first-names-only" />
                  <span className="text-sm text-[#a39797]">Use first names only on shared cards.</span>
                </label>
                <button onClick={submitSurvey} disabled={savingSurvey}
                  className="btn-primary disabled:opacity-50"
                  data-testid="survey-submit">
                  {savingSurvey ? <><Loader2 size={14} className="animate-spin" /> Saving</> : "Save my verdict"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Shares — only after a Great/Unforgettable rating */}
        {surveyDone && showShares && (
          <div className="card-noir p-6 mb-10" data-testid="share-section">
            <div className="text-xs tracking-widest text-[#d4af37] mb-3">POST IT WHILE YOU'RE STILL LAUGHING</div>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button onClick={nativeShare} className="btn-primary justify-center text-sm" data-testid="recap-native-share">
                <Share2 size={14} /> Share
              </button>
              <button onClick={copyCaption} className="btn-ghost justify-center text-sm" data-testid="recap-copy-caption">
                {copied ? <><CheckIcon size={14} /> Copied</> : <><Copy size={14} /> Copy caption</>}
              </button>
              <button onClick={() => downloadImage("story")} className="btn-ghost justify-center text-sm" data-testid="recap-save-story">
                <Download size={14} /> Save story (9:16)
              </button>
              <button onClick={() => downloadImage("square")} className="btn-ghost justify-center text-sm" data-testid="recap-save-square">
                <Download size={14} /> Save square (1:1)
              </button>
            </div>
            <p className="text-xs text-[#a39797]">
              Tag <span className="text-[#d4af37]">@nightbloodchronicles</span> · <span className="text-[#d4af37]">#WasntMe.Party</span>
            </p>
          </div>
        )}

        {/* Private feedback — only after an Okay/Not for us rating */}
        {surveyDone && !showShares && (
          <div className="card-noir p-6 mb-10" data-testid="feedback-section">
            <div className="text-xs tracking-widest text-[#d4af37] mb-3">TELL US WHAT WOULD'VE MADE IT BETTER</div>
            <textarea
              value={survey.comment}
              onChange={(e) => setSurvey((s) => ({ ...s, comment: e.target.value }))}
              placeholder="Anything we should know? This goes straight to Louis and nowhere else."
              className="input-noir min-h-[120px] text-sm mb-3"
              data-testid="feedback-comment" />
            <button onClick={submitSurvey} disabled={savingSurvey}
              className="btn-ghost text-sm disabled:opacity-50"
              data-testid="feedback-submit">
              {savingSurvey ? <Loader2 size={14} className="animate-spin" /> : "Send privately"}
            </button>
          </div>
        )}

        {/* Honest-review link — visible to EVERYONE, no steering */}
        <div className="card-noir p-5 mb-10 flex items-center justify-between" data-testid="public-review-strip">
          <div>
            <div className="font-display text-base mb-1 flex items-center gap-2"><Star size={16} className="text-[#d4af37]" /> Loved it? Honest reviews help.</div>
            <div className="text-xs text-[#a39797]">Leave a public review — good or bad — we won't filter it.</div>
          </div>
          <a href="https://www.google.com/search?q=wasntme.party+review" target="_blank" rel="noopener noreferrer"
            className="btn-ghost text-sm" data-testid="public-review-link">
            Leave a review <ExternalLink size={12} />
          </a>
        </div>

        {/* Referral code — always visible */}
        {recap.referral_code && (
          <div className="card-noir p-6 mb-10 border-[#d4af37]/40" data-testid="referral-section">
            <div className="text-xs tracking-widest text-[#d4af37] mb-2">YOUR $5-OFF REFERRAL CODE</div>
            <div className="flex items-center gap-3 mb-3">
              <div className="font-mono font-bold text-3xl text-[#f4f0ec]" data-testid="referral-code">{recap.referral_code}</div>
              <button onClick={copyRefLink} className="btn-ghost text-xs" data-testid="copy-ref-link">
                <Copy size={12} /> Copy link
              </button>
            </div>
            <p className="text-sm text-[#a39797] mb-3">
              Friends get <span className="text-[#f4f0ec]">$5 off</span> at checkout. After <span className="text-[#f4f0ec]">2 friends</span> book, we send you a <span className="text-[#d4af37]">free Dinner for Four Mini</span>.
            </p>
            <div className="flex items-center gap-2 text-xs text-[#a39797]">
              <Lock size={10} /> {recap.referral_count || 0} of 2 referrals so far
              {recap.free_mini_coupon && (
                <span className="ml-2 text-[#d4af37]">· free mini unlocked: <span className="font-mono">{recap.free_mini_coupon}</span></span>
              )}
            </div>
          </div>
        )}

        <div className="text-center">
          <Link to="/" className="btn-primary" data-testid="recap-host-again">Host another case →</Link>
        </div>
      </div>
    </div>
  );
};

export default RecapPage;
